/**
 * Le nom d'une couleur, pour nommer un calque (« Rectangle · jaune ») ou un point du color
 * picker (« Orange · #F28C28 ») : la couleur nommée la plus proche à l'œil — distance
 * euclidienne en OKLab — dans une courte palette. Un gris (chroma quasi nul) se nomme par sa
 * seule clarté, du blanc au noir : sinon un gris moyen tombait sur le marron ou le marine,
 * qui lui sont plus proches en distance que le gris et le noir de la palette. Les noms vivent
 * dans les locales (`composition_editor.layers.colors.<clé>`) ; ici, la clé.
 */

const PALETTE: [string, string][] = [
  ["yellow", "#F5C518"],
  ["mustard", "#C9A227"],
  ["orange", "#F28C28"],
  ["sand", "#D9A066"],
  ["coral", "#F2705A"],
  ["red", "#D93025"],
  ["burgundy", "#7F1D2D"],
  ["pink", "#F08CB4"],
  ["fuchsia", "#D61F8C"],
  ["amethyst", "#9B59D0"],
  ["violet", "#6D3FD0"],
  ["indigo", "#2E3B8F"],
  ["blue", "#2F6BEA"],
  ["navy", "#16284F"],
  ["sky", "#8EC3F2"],
  ["turquoise", "#1FB5B0"],
  ["green", "#2E9E5B"],
  ["mint", "#9BE0BB"],
  ["olive", "#7D7E2E"],
  ["beige", "#EADFC8"],
  ["brown", "#7A5230"],
  ["gray", "#8A8F98"],
  ["light_gray", "#D5D8DD"],
  ["dark_gray", "#4A4D52"],
  ["black", "#14161A"],
  ["white", "#FFFFFF"],
];

/** En deçà de ce chroma (OKLab), une couleur est un gris : son nom ne dépend que de sa clarté. */
const NEUTRAL_CHROMA = 0.03;

/** Les gris, du plus clair au plus sombre, avec la clarté (OKLab L) à partir de laquelle ils s'appliquent. */
const NEUTRALS: [string, number][] = [
  ["white", 0.93],
  ["light_gray", 0.76],
  ["gray", 0.55],
  ["dark_gray", 0.3],
  ["black", 0],
];

type Rgb = [number, number, number];
type Lab = [number, number, number];

/** #rgb, #rrggbb, #rrggbbaa ou rgb()/rgba() ; null pour le reste (dégradé, transparent…). */
export function parseColor(value: unknown): Rgb | null {
  if (typeof value !== "string") return null;
  const s = value.trim().toLowerCase();

  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s);
  if (short) return short.slice(1).map((h) => parseInt(h + h, 16)) as Rgb;

  const long = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/.exec(s);
  if (long) {
    if (long[4] === "00") return null;
    return long.slice(1, 4).map((h) => parseInt(h, 16)) as Rgb;
  }

  const fn = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+))?\s*\)$/.exec(s);
  if (fn) {
    if (fn[4] !== undefined && parseFloat(fn[4]) === 0) return null;
    return [fn[1], fn[2], fn[3]].map(Number) as Rgb;
  }
  return null;
}

/** sRGB 0-255 → OKLab (Björn Ottosson). */
function oklab([r, g, b]: Rgb): Lab {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const [lr, lg, lb] = [lin(r), lin(g), lin(b)];
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

const PALETTE_LAB: [string, Lab][] = PALETTE.map(([key, hex]) => [key, oklab(parseColor(hex)!)]);

/** La clé de la couleur nommée la plus proche, ou null si la valeur n'est pas une couleur unie. */
export function colorNameKey(value: unknown): string | null {
  const rgb = parseColor(value);
  if (!rgb) return null;

  const [L, A, B] = oklab(rgb);
  if (Math.hypot(A, B) < NEUTRAL_CHROMA) return NEUTRALS.find(([, from]) => L >= from)![0];

  let best: string | null = null;
  let bestDistance = Infinity;
  for (const [key, [l, a, b]] of PALETTE_LAB) {
    const d = (L - l) ** 2 + (A - a) ** 2 + (B - b) ** 2;
    if (d < bestDistance) { bestDistance = d; best = key; }
  }
  return best;
}

/** Une valeur qui ne peint rien : absente, `transparent`, `none`, ou d'alpha nul. Un dégradé
 *  (objet) n'est pas transparent. */
export function isTransparent(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value !== "string") return false;
  const v = value.trim().toLowerCase();
  if (v === "" || v === "transparent" || v === "none") return true;
  if (/^#[0-9a-f]{6}00$/.test(v)) return true;
  const fn = /^rgba?\(.*[\s,/]+([\d.]+)\s*\)$/.exec(v);
  return Boolean(fn && v.startsWith("rgba") && parseFloat(fn[1]) === 0);
}
